import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import EditFieldRenderer from "./EditFieldRenderer";
import { getFormattedArrayFields } from "./ArrayFieldSection";
import "./DynamicEditForm.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import WaveLoader from "../WaveLoader/WaveLoader";
import { getUser } from "../../pages/Login/authUtils";
import EditHistoryModal from "./EditHistoryModal";

const DynamicEditForm = ({ fields, apiUrl, successRedirect, roleCompare }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [dropdownData, setDropdownData] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [originalData, setOriginalData] = useState({});

  const { id } = useParams();
  const navigate = useNavigate();

  const user = getUser();

const SUPER_ADMIN_NAMES = new Set(["Super Admin"]);

console.log(apiUrl, successRedirect, roleCompare )

const isSuperAdmin = Array.isArray(user?.roles)
  ? user.roles.some((roleName) => SUPER_ADMIN_NAMES.has(roleName))
  : SUPER_ADMIN_NAMES.has(user?.role);

// ✅ Check for permission-based access
const hasUserEditPermission = Array.isArray(user?.permissions)
  ? user.permissions.includes("user:edit")
  : false;

// ✅ Final access check
const hasEditAuthority = isSuperAdmin || hasUserEditPermission;


  const currentUserId = user?._id;

  // ✅ Fetch current entry
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${apiUrl}/${id}`);
        const data = res.data;

        if (!data || Object.keys(data).length === 0) {
          toast.error("⛔ Entry not found or has been deleted.");
          navigate("/");
          return;
        }

        const updated = { ...data };
        fields
          .filter((f) => f.api && f.type === "ObjectId")
          .forEach((f) => {
            const value = data[f.fields];
            if (Array.isArray(value)) {
              updated[f.fields] = value.map((v) => v._id);
            } else if (value && typeof value === "object") {
              updated[f.fields] = value._id;
              updated[`${f.fields}_display`] =
                value[f.apiField] || value.name || `${value._id}`;
            }
          });

        if (!hasEditAuthority) {
          if (Array.isArray(roleCompare)) {
            const hasAccess = roleCompare.some(({ field }) => {
              const val = data[field];
              if (!val) return false;

              if (Array.isArray(val)) {
                return val.some((item) => {
                  const id = typeof item === "string" ? item : item?._id;
                  return id === currentUserId;
                });
              }

              const id = typeof val === "string" ? val : val?._id;
              return id === currentUserId;
            });

            if (!hasAccess) {
              toast.error("⛔ You are not authorized to edit this entry.");
              navigate("/");
              return;
            }
          } else {
            toast.error("⛔ You are not authorized to edit this entry.");
            navigate("/");
            return;
          }
        }

        setFormData(updated);
        setOriginalData(updated);
      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [id, apiUrl, fields]);

useEffect(() => {
  const fetchDropdowns = async () => {
    try {
      for (const field of fields) {
        if (!field.api) continue;

        const res = await axios.get(field.api);
        const entries = res.data;

        const labelField = field.apiField || "name"; // Default fallback to 'name'
        const allowedRoleIds = field.role
          ? Array.isArray(field.role)
            ? field.role.map(String)
            : [field.role.toString()]
          : [];

        // Optional filtering if role-based filtering is defined
        let filtered = entries;
        if (allowedRoleIds.length > 0) {
          filtered = entries.filter((entry) => {
            const entryRoleId =
              typeof entry.role === "object"
                ? entry.role._id?.toString()
                : entry.role?.toString();
            return entryRoleId && allowedRoleIds.includes(entryRoleId);
          });
        }

        // Build dropdown options from filtered entries
        const options = filtered.map((entry) => ({
          id: entry._id,
          label: entry[labelField] || `Unnamed (${entry._id})`,
        }));

        // Preserve selected items not in the current options
        const existingIds = Array.isArray(formData[field.fields])
          ? formData[field.fields]
          : [formData[field.fields]];

        for (const id of existingIds) {
          if (id && !options.find((opt) => opt.id === id)) {
            const match = entries.find((e) => e._id === id);
            options.unshift({
              id,
              label: match?.[labelField] || `Previously selected (${id})`,
            });
          }
        }

        setDropdownData((prev) => ({
          ...prev,
          [field.fields]: options,
        }));
      }
    } catch (err) {
      console.error("Dropdown fetch failed:", err);
      toast.error("⚠️ Failed to load dropdown options.");
    } finally {
      setDropdownsLoaded(true);
    }
  };

  if (!fetching) fetchDropdowns();
}, [fields, fetching, formData]);


  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
      [`${name}_display`]:
        dropdownData[name]?.find((i) => i.id === value)?.label || "",
    });
  };

  const handleArrayChange = (fieldName, index, subField, value) => {
    // ✅ If it's a checkbox-style array (no index or subfield)
    if (index === null && subField === null) {
      setFormData({ ...formData, [fieldName]: value });
      return;
    }

    // ✅ Regular nested array with subfields
    const updated = [...(formData[fieldName] || [])];
    if (!updated[index]) updated[index] = {};
    updated[index][subField] = value;
    setFormData({ ...formData, [fieldName]: updated });
  };

  const handleAddItem = (fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: [...(formData[fieldName] || []), {}],
    });
  };

  const handleRemoveItem = (fieldName, index) => {
    const updated = [...(formData[fieldName] || [])];
    updated.splice(index, 1);
    setFormData({ ...formData, [fieldName]: updated });
  };

  const getFieldDiffs = (original, updated) => {
    const changes = {};
    for (const key in updated) {
      const originalValue = original[key];
      const updatedValue = updated[key];
      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        changes[key] = { old: originalValue, new: updatedValue };
      }
    }
    return changes;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  const processedData = getFormattedArrayFields(formData, fields);
  const changes = getFieldDiffs(originalData, processedData);

  try {
    // ✅ Update entry
    await axios.put(`${apiUrl}/${id}`, processedData);

    // ✅ Try to post manual log if changes exist
    if (Object.keys(changes).length > 0 && user?._id) {
      try {
        await axios.post(`${apiUrl}/${id}/logs`, {
          userId: user._id,
          changes,
        });
      } catch (logErr) {
        if (logErr?.response?.status === 404) {
          console.warn("Log API not found — skipping log step.");
        } else {
          console.error("Log save failed:", logErr);
          toast.warning("⚠️ Entry saved, but logging failed.");
        }
      }
    }

    toast.success("✅ Entry updated");
    navigate(successRedirect);
  } catch (err) {
    console.error("Submission failed:", err);
    toast.error("❌ Update failed.");
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    const handleKey = (e) => {
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        handleSubmit(e);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [formData]);

  if (fetching || !dropdownsLoaded) return <WaveLoader />;

  return (
    <div className="dynamic-form">
      <h2 className="form-title">Edit Entry</h2>
      <ToastContainer position="top-right" autoClose={4000} />

      <div className="logs-button-top-right" title="View Edit Logs">
        <i
          className="fas fa-clipboard-list logs-icon"
          onClick={() => setShowLogs(true)}
        ></i>
      </div>

      <form onSubmit={handleSubmit} className="form-wrapper">
        {fields.map((field, idx) => (
          <EditFieldRenderer
            key={idx}
            field={field}
            formData={formData}
            setFormData={setFormData}
            dropdownData={dropdownData}
            handleInputChange={handleInputChange}
            handleDropdownChange={handleDropdownChange}
            handleArrayChange={handleArrayChange}
            handleAddItem={handleAddItem}
            handleRemoveItem={handleRemoveItem}
          />
        ))}
        <button type="submit" disabled={loading} className="submit-btn">
          {loading ? "Updating..." : "Update Entry"}
        </button>
        {successMessage && <p className="success-message">{successMessage}</p>}
        {showLogs && (
          <EditHistoryModal
            entryId={id}
            apiUrl={apiUrl} // ✅ pass this too
            onClose={() => setShowLogs(false)}
          />
        )}
      </form>
    </div>
  );
};

export default DynamicEditForm;
